import React from 'react';
import classNames from 'classnames';
import AsyncSelect from 'react-select/async';
import SyncSelect from 'react-select';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import NoSsr from '@material-ui/core/NoSsr';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import CancelIcon from '@material-ui/icons/Cancel';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { emphasize } from '@material-ui/core/styles/colorManipulator';
import store from '../store';
import { IconButton } from '@material-ui/core';
import styled from 'styled-components/macro';
// import CloseIcon from '@material-ui/icons/Close';

const StyledArrowDropDownIcon = styled(ArrowDropDownIcon)`
  color: rgba(0, 0, 0, 0.54);
  margin: -4px !important;  
`;

const useStyles = makeStyles(theme => {
  // theme = store.getState().content.themes.light;

  return {
    root: {
      flexGrow: 1,
      maxWidth: '100%',
      margin: props => props.root && props.root.margin ? props.root.margin : null
    },
    formControl: {
      overflow: props => props.formControl && props.formControl.overflow ? props.formControl.overflow : 'hidden'
    },
    input: {
      display: 'flex',
      fontFamily: theme.typography.fontFamily,
      height: props => props.input && props.input.height ? props.input.height : null,
      overflow: 'hidden'
    },
    valueContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      flex: 1,
      alignItems: 'center',
      overflow: 'visible',
      alignSelf: props => props.valueContainer && props.valueContainer.alignSelf ? props.valueContainer.alignSelf : null
    },
    chip: {
      margin: `${theme.spacing(1) / 2}px ${theme.spacing(1) / 4}px`
    },
    chipFocused: {
      backgroundColor: emphasize(
        theme.palette.type === 'light'
          ? theme.palette.grey[300]
          : theme.palette.grey[700],
        0.08
      )
    },
    noOptionsMessage: {
      padding: `${theme.spacing(1)}px ${theme.spacing(1) * 2}px`
    },
    singleValue: props => ({
      fontSize: props.singleValue && props.singleValue.fontSize ? props.singleValue.fontSize : 16
    }),
    placeholder: {
      position: 'absolute',
      paddingLeft: 6,
      marginRight: -6
    },
    paper: {
      position: 'absolute',
      zIndex: 1,
      marginTop: theme.spacing(1),
      left: 0,
      right: 0
    },
    divider: {
      height: theme.spacing(1) * 2
    }
  };
});

function NoOptionsMessage(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}

function Control(props) {
  return (
    <TextField
      fullWidth
      placeholder={props.selectProps.placeholder}
      className={props.selectProps.classes.formControl}
      InputProps={{
        inputComponent,
        inputProps: {
          className: props.selectProps.classes.input,
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps
        }
      }}
      {...props.selectProps.textFieldProps}
    />
  );
}

function Option(props) {
  return (
    <MenuItem
      buttonRef={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

function Placeholder(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.placeholder}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function SingleValue(props) {
  return (
    <Typography
      className={props.selectProps.classes.singleValue}
      {...props.innerProps}
      style={{
        whiteSpace: 'nowrap'
      }}
    >
      {props.children}
    </Typography>
  );
}

function ValueContainer(props) {
  return (
    <div className={props.selectProps.classes.valueContainer}>
      {props.children}
    </div>
  );
}

function MultiValue(props) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={classNames(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused
      })}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
}

function Menu(props) {
  return (
    <Paper
      square
      className={props.selectProps.classes.paper}
      {...props.innerProps}
    >
      {props.children}
    </Paper>
  );
}

function ClearIndicator(props) {
  const {
    children = <CancelIcon />,
    getStyles,
    innerProps: { ref, ...restInnerProps }
  } = props;

  return (
    <IconButton
      {...restInnerProps}
      style={getStyles('clearIndicator', props)}
      buttonRef={ref}
    >
      {children}
    </IconButton>
  );
}

const DropdownIndicator = props => {
  if (props && props.selectProps && props.selectProps.showDropdownArrow) {
    return (
      <StyledArrowDropDownIcon/>
    );
  }
  return null;
};

const defaultComponents = {
  ClearIndicator,
  Control,
  DropdownIndicator,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer
};

function IntegrationReactSelect({
  disabled,
  onChange = () => {},
  loadOptions = () => Promise.resolve([]),
  placeholder,
  multi,
  async = true,
  'data-testid': testId,
  styleOverrides = {},
  openMenuOnClick = false,
  showDropdownArrow = false,
  overrideComponents = {},
  ...SelectProps
}) {
  const classes = useStyles(styleOverrides);
  const theme = store.getState().content.themes.light;
  const [value, setValue] = React.useState(null);

  const components = { ...defaultComponents, ...overrideComponents};

  function handleChange(v) {
    setValue(v);
    onChange(v);
  }

  const selectStyles = {
    input: base => ({
      ...base,
      color: theme.palette.text.primary,
      '& input': {
        font: 'inherit'
      }
    })
  };

  const Select = async ? AsyncSelect : SyncSelect;

  return (
    <div className={classes.root}>
      <NoSsr>
        <Select
          classes={classes}
          styles={selectStyles}
          textFieldProps={{
            variant: 'outlined',
            'data-testid' : testId,
            InputLabelProps: {
              shrink: true
            }
          }}
          cacheOptions
          loadOptions={loadOptions}
          components={components}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          isDisabled={disabled}
          isMulti={multi}
          isClearable={true}
          menuPlacement="auto"
          noOptionsMessage={() => null}
          showDropdownArrow={showDropdownArrow}
          {...SelectProps}
        />
      </NoSsr>
    </div>
  );
}

export default IntegrationReactSelect;
